import React, { useState, useEffect, useCallback } from 'react';
import { apiGet, apiPost, apiDelete } from '../api';
import { PERM, useCan } from '../permissions';

const updateAccount = (payload) => apiPost('/api/admin/update-account', payload);
const updateBalance = (payload) => apiPost('/api/transaction/balance', payload);

const DarkModal = ({ show, onClose, children }) => {
    if (!show) return null;
    return (
        <div style={{
            position: 'fixed', inset: 0, zIndex: 200,
            background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)',
            display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 20
        }}>
            <div style={{
                background: '#13132a',
                border: '1px solid rgba(255,77,109,0.2)',
                borderRadius: 18, padding: '28px 24px',
                width: '90%', maxWidth: 480,
                boxShadow: '0 24px 64px rgba(0,0,0,0.7)',
                position: 'relative', maxHeight: '90vh', overflowY: 'auto'
            }}>
                <button onClick={onClose} style={{
                    position: 'absolute', top: 14, right: 18,
                    background: 'none', border: 'none',
                    color: 'rgba(255,255,255,0.4)', fontSize: 20,
                    cursor: 'pointer', lineHeight: 1
                }}>×</button>
                {children}
            </div>
        </div>
    );
};

const AllAgency = () => {
    const can = useCan();
    // Editing a balance means typing over the current figure, so it is only
    // offered to someone who is allowed to see it — the server strips Balance
    // from the payload without 'viewBalances', and a blank field would submit
    // as a reduction to zero.
    const canSeeBalance = can(PERM.VIEW_BALANCES);
    const canEditBalance = canSeeBalance && can(PERM.EDIT_BALANCE);
    const [agencies, setAgencies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [agencyToDelete, setAgencyToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [agencyToEdit, setAgencyToEdit] = useState(null);
    const [isUpdating, setIsUpdating] = useState(false);
    const [editFormData, setEditFormData] = useState({ name: '', email: '', newPassword: '', phone: '', Balance: 0 });

    const fetchAgencies = useCallback(async () => {
        try {
            const rows = await apiGet('/api/admin/accounts/agencies');
            setAgencies(rows.map(a => ({ uid: a.userId || a.id, ...a })));
        } catch {
            setError("Failed to load agencies.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchAgencies(); }, [fetchAgencies]);

    const filteredAgencies = agencies.filter(a =>
        a.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        a.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (a.ID && String(a.ID).toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const totalPages = Math.ceil(filteredAgencies.length / itemsPerPage);
    const indexOfFirst = (currentPage - 1) * itemsPerPage;
    const currentItems = filteredAgencies.slice(indexOfFirst, indexOfFirst + itemsPerPage);

    const handleOpenEditModal = (agency) => {
        setAgencyToEdit(agency);
        setEditFormData({ name: agency.name, email: agency.email, newPassword: '', phone: agency.phone || '', Balance: agency.Balance || 0 });
        setIsEditModalOpen(true);
    };

    const handleConfirmUpdate = async (e) => {
        e.preventDefault();
        if (!agencyToEdit) return;
        setIsUpdating(true);
        try {
            if (editFormData.newPassword && editFormData.newPassword.length < 6) {
                throw new Error("Password must be at least 6 characters long.");
            }
            // Profile doc (name/email/phone) + Firebase Auth (email/password) in one call.
            const res = await updateAccount({
                collection: 'agencies',
                uid: agencyToEdit.id,
                name: editFormData.name,
                email: editFormData.email,
                phone: editFormData.phone,
                newPassword: editFormData.newPassword || undefined,
            });
            if (res.success === false) throw new Error(res.message);
            if (canEditBalance && Number(editFormData.Balance) !== agencyToEdit.Balance) {
                const b = await updateBalance({ targetUid: agencyToEdit.id, targetCollection: 'agencies', newBalance: Number(editFormData.Balance) });
                if (b.success === false) throw new Error(b.message);
            }
            alert(`Agency '${agencyToEdit.name}' updated successfully!`);
            fetchAgencies();
        } catch (err) {
            alert(`Failed to update: ${err.message}`);
        } finally {
            setIsUpdating(false);
            setIsEditModalOpen(false);
        }
    };

    const handleConfirmDelete = async () => {
        if (!agencyToDelete) return;
        setIsDeleting(true);
        try {
            const res = await apiDelete(`/api/admin/accounts/agencies/${agencyToDelete.id}`);
            if (res.success === false) throw new Error(res.message);
            alert(`Agency '${agencyToDelete.name}' removed.`);
            fetchAgencies();
        } catch {
            alert("Failed to remove agency.");
        } finally {
            setIsDeleting(false);
            setIsDeleteModalOpen(false);
        }
    };

    if (loading) return <div className="dark-loading"><div className="spinner" /><p>Loading All Agencies...</p></div>;
    if (error) return <div className="dark-error">{error}</div>;

    return (
        <div className="dark-page">
            <h2>Manage All Agencies</h2>

            <div className="search-row">
                <input
                    type="text"
                    className="dark-input"
                    style={{ maxWidth: 380 }}
                    placeholder="Search by name, email, or user ID..."
                    value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                />
            </div>

            <div className="dark-table-wrap">
                <table className="dark-table">
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>Name</th>
                            <th>User ID</th>
                            <th>Email</th>
                            <th>Phone</th>
                            {canSeeBalance && <th className="text-right">Balance</th>}
                            <th className="text-center">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentItems.map((agency, index) => (
                            <tr key={agency.id}>
                                <td className="text-muted">{indexOfFirst + index + 1}</td>
                                <td className="font-medium">{agency.name}</td>
                                <td className="text-muted">{agency.ID}</td>
                                <td className="text-muted">{agency.email}</td>
                                <td className="text-muted">{agency.phone || 'N/A'}</td>
                                {canSeeBalance && <td className="text-right text-green">{agency.Balance?.toFixed(2) || '0.00'}</td>}
                                <td className="text-center" style={{ whiteSpace: 'nowrap' }}>
                                    <button className="btn-action edit" style={{ marginRight: 6 }} onClick={() => handleOpenEditModal(agency)} title="Edit">
                                        <i className="fas fa-edit" />
                                    </button>
                                    {can(PERM.DELETE_ACCOUNTS) && (
                                        <button className="btn-action delete" onClick={() => { setAgencyToDelete(agency); setIsDeleteModalOpen(true); }} title="Delete">
                                            <i className="fas fa-trash-alt" />
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                        {currentItems.length === 0 && (
                            <tr className="empty-row"><td colSpan="7">{searchTerm ? 'No agencies matching your search.' : 'No agencies found.'}</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {filteredAgencies.length > itemsPerPage && (
                <div className="dark-pagination">
                    <button className="btn-page" onClick={() => setCurrentPage(p => p - 1)} disabled={currentPage === 1}>Previous</button>
                    <span>Page {currentPage} of {totalPages}</span>
                    <button className="btn-page" onClick={() => setCurrentPage(p => p + 1)} disabled={currentPage === totalPages}>Next</button>
                </div>
            )}

            {/* Delete Modal */}
            <DarkModal show={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)}>
                <div className="dark-confirm">
                    <div className="warn-icon"><i className="fas fa-exclamation-triangle" /></div>
                    <h3>Confirm Deletion</h3>
                    <p>
                        Permanently delete agency <strong>{agencyToDelete?.name}</strong>?
                        This will remove their account and all transaction history.
                        <br /><strong style={{ color: '#fca5a5' }}>This action cannot be undone.</strong>
                    </p>
                    <div className="btn-row">
                        <button className="btn-page" onClick={() => setIsDeleteModalOpen(false)} disabled={isDeleting}>Cancel</button>
                        <button className="btn-action delete" style={{ padding: '9px 20px', fontSize: 13 }} onClick={handleConfirmDelete} disabled={isDeleting}>
                            {isDeleting ? <><i className="fas fa-spinner fa-spin" /> Deleting...</> : 'Confirm Delete'}
                        </button>
                    </div>
                </div>
            </DarkModal>

            {/* Edit Modal */}
            <DarkModal show={isEditModalOpen} onClose={() => setIsEditModalOpen(false)}>
                <h2 style={{ marginBottom: 16 }}>Edit Agency</h2>
                <form onSubmit={handleConfirmUpdate} className="dark-form">
                    <label>Name</label>
                    {/* Same 16-character limit as creation — the name shows on
                        a narrow strip beside the agency photo in the app. */}
                    <input type="text" maxLength={16} value={editFormData.name} onChange={e => setEditFormData(p => ({ ...p, name: e.target.value }))} required />
                    <label>Email</label>
                    <input type="email" value={editFormData.email} onChange={e => setEditFormData(p => ({ ...p, email: e.target.value }))} required />
                    <label>Phone</label>
                    <input type="tel" value={editFormData.phone} onChange={e => setEditFormData(p => ({ ...p, phone: e.target.value }))} />
                    <label>New Password</label>
                    <input type="password" value={editFormData.newPassword} onChange={e => setEditFormData(p => ({ ...p, newPassword: e.target.value }))} placeholder="Leave blank to keep current" />
                    {canEditBalance && <>
                        <label>Balance</label>
                        <input type="number" value={editFormData.Balance} onChange={e => setEditFormData(p => ({ ...p, Balance: e.target.value }))} min="0" />
                    </>}
                    <div className="btn-row">
                        <button type="button" className="btn-page" onClick={() => setIsEditModalOpen(false)} disabled={isUpdating}>Cancel</button>
                        <button type="submit" className="btn-action edit" style={{ padding: '9px 20px', fontSize: 13 }} disabled={isUpdating}>
                            {isUpdating ? 'Updating...' : 'Save Changes'}
                        </button>
                    </div>
                </form>
            </DarkModal>
        </div>
    );
};

export default AllAgency;
